import { cn } from '@/lib/utils';
import React, { useRef, useState } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';

/**
 * The final-step Submit control for public forms, with Cloudflare Turnstile
 * as a **popover on Submit** (design: Preview Frame Redesign, turn 6).
 *
 * The footer never reflows. The widget runs **invisibly in the background**
 * (`interaction-only`) while the person fills; for the common case it passes
 * silently and the footer is just a muted "Protected by Cloudflare" line + an
 * enabled Submit. When Cloudflare wants a human action, tapping Submit floats
 * the widget in a popover **above** the button (form dimmed behind); once it
 * clears it submits automatically.
 *
 * The token itself is still consumed by the host at draft-creation time — this
 * component only owns the challenge UX and hands the token up via `onToken`.
 */

const ACCENT = '#2f6fdb';

const SUBMIT_BASE =
  'inline-flex items-center justify-center gap-[7px] overflow-hidden rounded-[10px] px-[16px] py-[9px] text-[13px] font-extrabold text-white transition-[background-color,box-shadow]';

const Spinner: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    className={className}
    style={{ animation: 'ts-spin .9s linear infinite' }}
  >
    <circle cx="12" cy="12" r="9" stroke="rgba(255,255,255,.4)" strokeWidth="3" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const ShieldIcon: React.FC = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" />
  </svg>
);

interface Props {
  enabled: boolean;
  siteKey: string;
  token: string | null;
  onToken: (token: string | null) => void;
  onSubmit: () => void | Promise<void>;
  submitting: boolean;
  label?: string;
}

const TurnstileSubmit: React.FC<Props> = ({
  enabled,
  siteKey,
  token,
  onToken,
  onSubmit,
  submitting,
  label,
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  // When the filler tapped Submit without a token, auto-submit as soon as the
  // challenge clears.
  const autoSubmitRef = useRef(false);

  const submitLabel = label || 'Submit';

  const doSubmit = () => {
    void onSubmit();
  };

  const handleClick = () => {
    if (submitting) return;
    // Silent-pass / no protection: submit straight away.
    if (!enabled || token) {
      doSubmit();
      return;
    }
    // Challenge path: reveal the widget in the popover and submit once it clears.
    autoSubmitRef.current = true;
    setPopoverOpen(true);
  };

  const handleSuccess = (t: string) => {
    onToken(t);
    if (autoSubmitRef.current) {
      autoSubmitRef.current = false;
      setPopoverOpen(false);
      doSubmit();
    }
  };

  const submitStyle: React.CSSProperties = submitting
    ? { backgroundColor: '#7ea8e6', cursor: 'progress' }
    : {
        backgroundColor: ACCENT,
        boxShadow: popoverOpen
          ? `0 0 0 4px ${ACCENT}24`
          : '0 2px 6px rgba(47,111,219,.3)',
        cursor: 'pointer',
      };

  return (
    <div className="relative flex w-full flex-wrap items-center justify-end gap-x-[12px] gap-y-[8px] [.fill-bar-narrow_&]:order-first [.fill-bar-narrow_&]:basis-full">
      {/* Resting reassurance line (hidden while the challenge popover is up).
          In the phone action bar it drops under the full-width button,
          centred, beside the attribution. */}
      {!popoverOpen && (
        <span className="inline-flex items-center gap-[10px] text-[10px] font-semibold text-[#9aa3b0] [.fill-bar-narrow_&]:order-2 [.fill-bar-narrow_&]:w-full [.fill-bar-narrow_&]:justify-center">
          {enabled && (
            <span className="inline-flex items-center gap-[5px]">
              <ShieldIcon />
              Protected by Cloudflare
            </span>
          )}
          <a
            href="https://eaco.me"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden items-center gap-[4px] [.fill-bar-narrow_&]:inline-flex sm:[.fill-bar-narrow_&]:hidden"
          >
            Powered by <span className="font-extrabold text-[#6b7c9a]">eaco</span>
          </a>
        </span>
      )}

      <button
        type="button"
        onClick={handleClick}
        disabled={submitting}
        className={cn(SUBMIT_BASE, '[.fill-bar-narrow_&]:h-[48px] [.fill-bar-narrow_&]:w-full [.fill-bar-narrow_&]:text-[15px]')}
        style={submitStyle}
      >
        {submitting ? (
          <>
            <Spinner />
            <span className="truncate">Verifying…</span>
          </>
        ) : (
          <span className="truncate">{submitLabel}</span>
        )}
      </button>

      {/* Turnstile host — always mounted while enabled so it can pass silently
          in the background; styled into a popover above Submit only when a
          challenge is actually needed. Toggling opacity (not mount) keeps the
          widget's background progress. */}
      {enabled && (
        <>
          {popoverOpen && (
            <div
              onClick={() => setPopoverOpen(false)}
              className="fixed inset-0 z-[40]"
              style={{ background: 'rgba(16,24,40,.14)' }}
            />
          )}
          <div
            className="absolute right-0 z-[50] w-[288px] rounded-[12px] border border-[#e6e9ee] bg-white p-[13px] shadow-[0_20px_44px_-12px_rgba(16,24,40,.36)] transition-[opacity,transform]"
            style={{
              bottom: 'calc(100% + 10px)',
              opacity: popoverOpen ? 1 : 0,
              transform: popoverOpen ? 'translateY(0)' : 'translateY(6px)',
              pointerEvents: popoverOpen ? 'auto' : 'none',
            }}
            aria-hidden={!popoverOpen}
          >
            <div className="mb-[9px] text-[12px] font-extrabold text-[#1f2a3d]">
              Quick check before you submit
            </div>
            <Turnstile
              siteKey={siteKey}
              onSuccess={handleSuccess}
              onExpire={() => onToken(null)}
              onError={() => onToken(null)}
              options={{ theme: 'light', appearance: 'interaction-only' }}
            />
            {/* tail pointing down at Submit */}
            <div
              className="absolute h-[13px] w-[13px] rotate-45 border-b border-r border-[#e6e9ee] bg-white"
              style={{ right: '44px', bottom: '-7px' }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default TurnstileSubmit;
